import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import { hydrateSymbolPipPositions } from '@/lib/symbol-pip-position';

// ─── Types ────────────────────────────────────────────────────────

interface CtraderAccount {
  id: number;
  account_id: string;
  account_name: string;
  balance: number;
  currency: string;
  is_live: boolean;
}

interface Strategy {
  id: string;
  name: string;
  symbol: string;
  timeframe: string;
  assigned_accounts: Array<{ account_id: string; account_name: string; platform: string }>;
  is_active: boolean;
}

interface SymbolEntry {
  symbolId: number;
  symbolName: string;
  digits: number;
  pipPosition?: number | null;
}

interface SymbolList {
  id: string;
  name: string;
  ctrader_account_id: string | null;
  strategy_id: string | null;
  symbols: SymbolEntry[];
  created_at: string;
  updated_at: string;
}

interface AvailableSymbol {
  symbolId: number;
  name: string;
  description?: string;
  digits: number;
  pipPosition?: number | null;
}

// ─── Helpers ──────────────────────────────────────────────────────

function cn(...classes: (string | boolean | undefined | null)[]): string {
  return classes.filter(Boolean).join(' ');
}

// ─── Component ────────────────────────────────────────────────────

interface SymbolListPanelProps {
  selectedCtraderAccountId: string | null;
  ctraderAccounts: CtraderAccount[];
  strategies: Strategy[];
  onListChange?: () => void;
}

export default function SymbolListPanel({
  selectedCtraderAccountId,
  ctraderAccounts,
  strategies,
  onListChange,
}: SymbolListPanelProps) {
  const [symbolLists, setSymbolLists] = useState<SymbolList[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Create / Edit state
  const [showForm, setShowForm] = useState(false);
  const [editingListId, setEditingListId] = useState<string | null>(null);
  const [formName, setFormName] = useState('');
  const [formCtraderAccountId, setFormCtraderAccountId] = useState<string>('');
  const [formStrategyId, setFormStrategyId] = useState<string>('');
  const [formSaving, setFormSaving] = useState(false);

  // Symbol browsing
  const [availableSymbols, setAvailableSymbols] = useState<AvailableSymbol[]>([]);
  const [symbolsLoading, setSymbolsLoading] = useState(false);
  const [showSymbolBrowser, setShowSymbolBrowser] = useState<string | null>(null); // list id to browse for
  const [symbolSearch, setSymbolSearch] = useState('');

  // Delete confirm
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  // ─── Expanded list IDs (which lists are showing full symbol chips) ───
  const [expandedListIds, setExpandedListIds] = useState<Set<string>>(new Set());

  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // ─── Fetch symbol lists ──────────────────────────────────────

  const fetchLists = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setSymbolLists([]);
        setLoading(false);
        return;
      }

      const { data, error: dbError } = await supabase
        .from('symbol_lists')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (dbError) throw dbError;

      if (data && data.length > 0) {
        setSymbolLists(data.map((row: Record<string, unknown>) => ({
          id: row.id as string,
          name: row.name as string,
          ctrader_account_id: (row.ctrader_account_id as string) || null,
          strategy_id: (row.strategy_id as string) || null,
          symbols: (row.symbols as SymbolEntry[]) || [],
          created_at: row.created_at as string,
          updated_at: row.updated_at as string,
        })));
      } else {
        setSymbolLists([]);
      }
    } catch (err) {
      console.error('Failed to fetch symbol lists:', err);
      setError('Could not load symbol lists from server.');
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLists();
  }, [fetchLists]);

  // ─── Save list (create or update) ────────────────────────────

  const saveList = async () => {
    if (!formName.trim()) return;
    setFormSaving(true);

    let symbols: SymbolEntry[] = editingListId
      ? (symbolLists.find((l) => l.id === editingListId)?.symbols || [])
      : [];

    // Fill the broker's REAL pipPosition/digits BEFORE saving, so a list is
    // never stored with a blank pipPosition (and later sessions never need to
    // query the broker again). Falls back to the current values on failure.
    const accountForPip = formCtraderAccountId || selectedCtraderAccountId || '';
    if (
      accountForPip &&
      symbols.length > 0 &&
      symbols.some((s) => s.pipPosition === null || s.pipPosition === undefined)
    ) {
      try {
        symbols = (await hydrateSymbolPipPositions(accountForPip, symbols)) as SymbolEntry[];
      } catch { /* keep existing values — never block the save */ }
    }

    const payload = {
      name: formName.trim(),
      ctrader_account_id: formCtraderAccountId || null,
      strategy_id: formStrategyId || null,
      symbols,
    };

    try {
      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {
        setError('You must be logged in to save symbol lists.');
        setFormSaving(false);
        return;
      }

      if (editingListId) {
        // Update existing
        const { error: dbError } = await supabase
          .from('symbol_lists')
          .update({ ...payload, updated_at: new Date().toISOString() })
          .eq('id', editingListId);

        if (dbError) throw dbError;

        setSymbolLists(symbolLists.map((l) =>
          l.id === editingListId ? { ...l, ...payload, updated_at: new Date().toISOString() } : l
        ));
      } else {
        // Create new
        const { data, error: dbError } = await supabase
          .from('symbol_lists')
          .insert({ ...payload, user_id: user.id })
          .select()
          .single();

        if (dbError) throw dbError;

        const newList: SymbolList = {
          id: (data as Record<string, unknown>).id as string,
          name: payload.name,
          ctrader_account_id: payload.ctrader_account_id,
          strategy_id: payload.strategy_id,
          symbols: payload.symbols,
          created_at: (data as Record<string, unknown>).created_at as string,
          updated_at: (data as Record<string, unknown>).updated_at as string,
        };
        setSymbolLists([newList, ...symbolLists]);
      }

      resetForm();
      onListChange?.();
    } catch (err) {
      console.error('Failed to save symbol list:', err);
      setError('Failed to save list.');
    } finally {
      if (mountedRef.current) setFormSaving(false);
    }
  };

  // ─── Delete list ─────────────────────────────────────────────

  const deleteList = async (id: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        await supabase.from('symbol_lists').delete().eq('id', id);
      }
      setSymbolLists(symbolLists.filter((l) => l.id !== id));
      // Remove from expanded set if present
      setExpandedListIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    } catch { /* silent */ }
    setDeleteConfirmId(null);
  };

  // ─── Add / Remove symbol from list ───────────────────────────

  const addSymbolToList = async (listId: string, sym: AvailableSymbol) => {
    const entry: SymbolEntry = {
      symbolId: sym.symbolId,
      symbolName: sym.name,
      digits: sym.digits,
      pipPosition: sym.pipPosition,
    };
    const updated = symbolLists.map((l) => {
      if (l.id !== listId) return l;
      if (l.symbols.some((s) => s.symbolId === sym.symbolId)) return l;
      return { ...l, symbols: [...l.symbols, entry], updated_at: new Date().toISOString() };
    });

    setSymbolLists(updated);

    // Sync to Supabase
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const target = updated.find((l) => l.id === listId);
        if (target) {
          await supabase.from('symbol_lists').update({ symbols: target.symbols, updated_at: target.updated_at }).eq('id', listId);
        }
      }
    } catch { /* silent */ }
  };

  const removeSymbolFromList = async (listId: string, symbolId: number) => {
    const updated = symbolLists.map((l) => {
      if (l.id !== listId) return l;
      return { ...l, symbols: l.symbols.filter((s) => s.symbolId !== symbolId), updated_at: new Date().toISOString() };
    });

    setSymbolLists(updated);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const target = updated.find((l) => l.id === listId);
        if (target) {
          await supabase.from('symbol_lists').update({ symbols: target.symbols, updated_at: target.updated_at }).eq('id', listId);
        }
      }
    } catch { /* silent */ }
  };

  // ─── Fetch available symbols from cTrader ────────────────────

  const fetchAvailableSymbols = useCallback(async () => {
    const accountId = formCtraderAccountId || selectedCtraderAccountId;
    if (!accountId) return;
    setSymbolsLoading(true);
    try {
      const { data } = await supabase.functions.invoke<{
        success: boolean;
        symbolsRes: Record<string, unknown> | null;
      }>('ctrader-gateway', {
        method: 'POST',
        body: {
          action: 'get-symbols-list',
          accountId: parseInt(accountId, 10),
          includeSymbolsWithoutTickets: true,
        },
      });
      if (data?.success && data.symbolsRes) {
        const symArray = Array.isArray(data.symbolsRes.symbol)
          ? data.symbolsRes.symbol as Array<Record<string, unknown>>
          : [];
        const mapped = symArray
          .map((s) => ({
            symbolId: typeof s.symbolId === 'number' ? s.symbolId : (typeof s.symbolId === 'string' ? parseInt(s.symbolId, 10) : 0),
            name: typeof s.name === 'string' ? s.name : (typeof s.symbolName === 'string' ? s.symbolName : 'Unknown'),
            description: typeof s.description === 'string' ? s.description : undefined,
            digits: typeof s.digits === 'number' ? s.digits : 5,
            pipPosition: typeof s.pipPosition === 'number' ? s.pipPosition : null,
          }))
          .filter((s) => s.symbolId > 0)
          .sort((a, b) => a.name.localeCompare(b.name));
        setAvailableSymbols(mapped);
      }
    } catch { /* ignore */ } finally {
      if (mountedRef.current) setSymbolsLoading(false);
    }
  }, [formCtraderAccountId, selectedCtraderAccountId]);

  // ─── Form handlers ───────────────────────────────────────────

  const resetForm = () => {
    setShowForm(false);
    setEditingListId(null);
    setFormName('');
    setFormCtraderAccountId(selectedCtraderAccountId || '');
    setFormStrategyId('');
  };

  const startCreate = () => {
    setEditingListId(null);
    setFormName('');
    setFormCtraderAccountId(selectedCtraderAccountId || '');
    setFormStrategyId('');
    setShowForm(true);
  };

  const startEdit = (list: SymbolList) => {
    setEditingListId(list.id);
    setFormName(list.name);
    setFormCtraderAccountId(list.ctrader_account_id || '');
    setFormStrategyId(list.strategy_id || '');
    setShowForm(true);
  };

  // ─── Toggle expanded list ───────────────────────────────────
  const toggleExpanded = (listId: string) => {
    setExpandedListIds((prev) => {
      const next = new Set(prev);
      if (next.has(listId)) {
        next.delete(listId);
      } else {
        next.add(listId);
      }
      return next;
    });
  };

  // ─── Filtered strategies for selected cTrader account ────────

  const filteredStrategies = formCtraderAccountId
    ? strategies.filter((s) =>
        s.assigned_accounts.some(
          (aa) => aa.account_id === formCtraderAccountId || aa.account_id === String(formCtraderAccountId)
        )
      )
    : strategies;

  // ─── Filtered symbol lists for current account ───────────────
  // NOTE: We no longer filter by selected account. The right panel is a fixed,
  // independent display that shows ALL symbol lists regardless of which
  // Spotware account tab is active on the left side.

  const filteredLists = symbolLists;

  // ─── Get account/strategy names ──────────────────────────────

  const getAccountName = (accountId: string | null) => {
    if (!accountId) return 'All accounts';
    const acc = ctraderAccounts.find(
      (a) => a.account_id === accountId || String(a.id) === accountId
    );
    return acc ? acc.account_name : accountId;
  };

  const getStrategyName = (strategyId: string | null) => {
    if (!strategyId) return 'Any strategy';
    const s = strategies.find((st) => st.id === strategyId);
    return s ? s.name : strategyId;
  };

  // ─── Filter available symbols by search ──────────────────────

  const filteredAvailableSymbols = symbolSearch
    ? availableSymbols.filter(
        (s) =>
          s.name.toLowerCase().includes(symbolSearch.toLowerCase()) ||
          String(s.symbolId).includes(symbolSearch)
      )
    : availableSymbols;

  // ─── Render ──────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="bg-background-100 rounded-xl border border-background-200/70 overflow-hidden">
        <div className="px-5 py-3.5 border-b border-background-200/70">
          <div className="h-4 w-32 bg-background-200/70 rounded animate-pulse"></div>
        </div>
        <div className="p-5">
          <div className="h-20 bg-background-200/50 rounded animate-pulse"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-background-100 rounded-xl border border-background-200/70 overflow-hidden">
      {/* Header */}
      <div className="px-5 py-3.5 border-b border-background-200/70 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-accent-500/10 flex items-center justify-center">
            <i className="ri-list-check-2 text-accent-400 text-sm"></i>
          </div>
          <div>
            <h3 className="text-sm font-heading font-semibold text-foreground-900">List of Symbols</h3>
            <p className="text-[11px] text-foreground-500">
              {filteredLists.length} list{filteredLists.length !== 1 ? 's' : ''} · {filteredLists.reduce((sum, l) => sum + l.symbols.length, 0)} symbols
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={startCreate}
          className="flex items-center gap-1.5 px-3 py-2 bg-accent-500 hover:bg-accent-600 text-background-50 rounded-lg text-xs font-medium transition-all cursor-pointer whitespace-nowrap"
        >
          <i className="ri-add-line"></i>
          New List
        </button>
      </div>

      <div className="p-5 space-y-4">
        {/* Error */}
        {error && (
          <div className="bg-amber-500/5 border border-amber-500/10 rounded-lg p-3 flex items-start gap-2">
            <i className="ri-error-warning-line text-amber-400 text-xs mt-0.5"></i>
            <p className="text-amber-600 text-xs">{error}</p>
          </div>
        )}

        {/* Create / Edit form */}
        {showForm && (
          <div className="bg-background-50 rounded-lg border border-background-200/70 p-4 space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="text-xs font-heading font-semibold text-foreground-900">
                {editingListId ? 'Edit List' : 'Create New List'}
              </h4>
              <button
                type="button"
                onClick={resetForm}
                className="text-foreground-400 hover:text-red-400 cursor-pointer"
              >
                <i className="ri-close-line"></i>
              </button>
            </div>

            {/* Name */}
            <div>
              <label className="text-[10px] font-medium text-foreground-500 uppercase tracking-wide mb-1 block">List Name</label>
              <input
                type="text"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                placeholder="e.g. Gold Scalping Symbols"
                className="w-full px-3 py-2 bg-background-100 border border-background-300/60 rounded-lg text-xs text-foreground-900 focus:outline-none focus:border-accent-500/50"
              />
            </div>

            {/* cTrader Account */}
            <div>
              <label className="text-[10px] font-medium text-foreground-500 uppercase tracking-wide mb-1 block">cTrader Account</label>
              <select
                value={formCtraderAccountId}
                onChange={(e) => setFormCtraderAccountId(e.target.value)}
                className="w-full px-3 py-2 bg-background-100 border border-background-300/60 rounded-lg text-xs text-foreground-900 focus:outline-none focus:border-accent-500/50 cursor-pointer"
              >
                <option value="">Any account</option>
                {ctraderAccounts.map((acc) => (
                  <option key={acc.account_id} value={acc.account_id}>
                    {acc.account_name} ({acc.is_live ? 'Live' : 'Demo'})
                  </option>
                ))}
              </select>
            </div>

            {/* Strategy */}
            <div>
              <label className="text-[10px] font-medium text-foreground-500 uppercase tracking-wide mb-1 block">Linked Strategy</label>
              <select
                value={formStrategyId}
                onChange={(e) => setFormStrategyId(e.target.value)}
                className="w-full px-3 py-2 bg-background-100 border border-background-300/60 rounded-lg text-xs text-foreground-900 focus:outline-none focus:border-accent-500/50 cursor-pointer"
              >
                <option value="">Any strategy</option>
                {filteredStrategies.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name} ({s.symbol} · {s.timeframe})
                  </option>
                ))}
              </select>
            </div>

            {/* Save button */}
            <div className="flex items-center gap-2 pt-1">
              <button
                type="button"
                onClick={saveList}
                disabled={!formName.trim() || formSaving}
                className="flex items-center gap-1.5 px-4 py-2 bg-accent-500 hover:bg-accent-600 text-background-50 rounded-lg text-xs font-medium transition-all cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {formSaving ? (
                  <><i className="ri-loader-4-line animate-spin"></i> Saving...</>
                ) : (
                  <><i className="ri-check-line"></i> {editingListId ? 'Save Changes' : 'Create List'}</>
                )}
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="px-4 py-2 text-xs text-foreground-500 hover:text-foreground-700 cursor-pointer whitespace-nowrap"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Empty state */}
        {filteredLists.length === 0 && !showForm && (
          <div className="text-center py-8">
            <div className="w-12 h-12 rounded-xl bg-background-200/70 flex items-center justify-center mx-auto mb-3">
              <i className="ri-list-indefinite text-foreground-400 text-xl"></i>
            </div>
            <p className="text-foreground-600 text-sm font-medium">No symbol lists yet</p>
            <p className="text-foreground-500 text-xs mt-1 max-w-xs mx-auto">
              Create a named list of symbols to organize your trading pairs for each strategy and account.
            </p>
            <button
              type="button"
              onClick={startCreate}
              className="mt-4 flex items-center gap-1.5 px-4 py-2 bg-accent-500 hover:bg-accent-600 text-background-50 rounded-lg text-xs font-medium transition-all cursor-pointer whitespace-nowrap mx-auto"
            >
              <i className="ri-add-line"></i>
              Create First List
            </button>
          </div>
        )}

        {/* Symbol lists */}
        {filteredLists.map((list) => {
          const isExpanded = expandedListIds.has(list.id);
          return (
            <div
              key={list.id}
              className="bg-background-50 rounded-lg border border-background-200/70 overflow-hidden"
            >
              {/* List header */}
              <div className="px-4 py-3 flex items-center justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-6 h-6 rounded-md bg-accent-500/10 flex items-center justify-center shrink-0">
                      <i className="ri-folder-3-line text-accent-400 text-xs"></i>
                    </div>
                    <h4 className="text-xs font-heading font-semibold text-foreground-900 truncate">{list.name}</h4>
                    <span className="text-[10px] text-foreground-400 font-mono shrink-0">{list.symbols.length} symbols</span>
                  </div>
                  <div className="flex items-center gap-2 text-[10px] text-foreground-500 pl-8">
                    {list.ctrader_account_id && (
                      <span className="flex items-center gap-1">
                        <i className="ri-user-3-line"></i>
                        {getAccountName(list.ctrader_account_id)}
                      </span>
                    )}
                    {list.strategy_id && (
                      <>
                        <span className="text-foreground-300">·</span>
                        <span className="flex items-center gap-1">
                          <i className="ri-flashlight-line"></i>
                          {getStrategyName(list.strategy_id)}
                        </span>
                      </>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    type="button"
                    onClick={() => setShowSymbolBrowser(showSymbolBrowser === list.id ? null : list.id)}
                    className="w-7 h-7 flex items-center justify-center rounded-md text-foreground-400 hover:text-accent-400 hover:bg-accent-500/10 transition-all cursor-pointer"
                    title="Add symbols"
                  >
                    <i className="ri-add-circle-line text-sm"></i>
                  </button>
                  <button
                    type="button"
                    onClick={() => startEdit(list)}
                    className="w-7 h-7 flex items-center justify-center rounded-md text-foreground-400 hover:text-foreground-600 hover:bg-background-200/70 transition-all cursor-pointer"
                    title="Edit list"
                  >
                    <i className="ri-pencil-line text-xs"></i>
                  </button>
                  <button
                    type="button"
                    onClick={() => setDeleteConfirmId(deleteConfirmId === list.id ? null : list.id)}
                    className="w-7 h-7 flex items-center justify-center rounded-md text-foreground-400 hover:text-red-400 hover:bg-red-500/10 transition-all cursor-pointer"
                    title="Delete list"
                  >
                    <i className="ri-delete-bin-line text-xs"></i>
                  </button>
                </div>
              </div>

              {/* Delete confirm */}
              {deleteConfirmId === list.id && (
                <div className="px-4 pb-2">
                  <div className="bg-red-500/5 border border-red-500/10 rounded-lg p-3 flex items-center gap-3">
                    <i className="ri-error-warning-line text-red-400 text-sm"></i>
                    <p className="text-red-400 text-xs flex-1">Delete &quot;{list.name}&quot;? This cannot be undone.</p>
                    <button
                      type="button"
                      onClick={() => deleteList(list.id)}
                      className="px-3 py-1.5 bg-red-500 hover:bg-red-600 text-background-50 rounded-md text-[10px] font-medium cursor-pointer whitespace-nowrap"
                    >
                      Delete
                    </button>
                    <button
                      type="button"
                      onClick={() => setDeleteConfirmId(null)}
                      className="px-3 py-1.5 text-[10px] text-foreground-500 hover:text-foreground-700 cursor-pointer whitespace-nowrap"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* Symbol browser */}
              {showSymbolBrowser === list.id && (
                <div className="px-4 pb-3">
                  <div className="bg-background-100 rounded-lg border border-background-200/70 p-3">
                    <div className="flex items-center gap-2 mb-2">
                      <button
                        type="button"
                        onClick={fetchAvailableSymbols}
                        disabled={symbolsLoading}
                        className="flex items-center gap-1 px-2.5 py-1.5 bg-accent-500 hover:bg-accent-600 text-background-50 rounded-md text-[10px] font-medium cursor-pointer whitespace-nowrap disabled:opacity-50"
                      >
                        {symbolsLoading ? (
                          <><i className="ri-loader-4-line animate-spin"></i> Loading...</>
                        ) : (
                          <><i className="ri-download-cloud-2-line"></i> Fetch Symbols</>
                        )}
                      </button>
                      <input
                        type="text"
                        value={symbolSearch}
                        onChange={(e) => setSymbolSearch(e.target.value)}
                        placeholder="Search symbol..."
                        className="flex-1 px-2.5 py-1.5 bg-background-50 border border-background-300/60 rounded-md text-[10px] text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
                      />
                    </div>
                    {filteredAvailableSymbols.length > 0 && (
                      <div className="max-h-40 overflow-y-auto space-y-0.5">
                        {filteredAvailableSymbols.slice(0, 30).map((sym) => {
                          const alreadyInList = list.symbols.some((s) => s.symbolId === sym.symbolId);
                          return (
                            <div
                              key={sym.symbolId}
                              className="flex items-center justify-between px-2 py-1 rounded hover:bg-background-200/50 transition-colors"
                            >
                              <div className="flex items-center gap-2">
                                <span className="text-foreground-900 text-[11px] font-mono font-medium">{sym.name}</span>
                                <span className="text-foreground-500 text-[9px] font-mono">ID:{sym.symbolId}</span>
                                <span className="text-foreground-400 text-[9px]">d:{sym.digits}</span>
                              </div>
                              <button
                                type="button"
                                onClick={() => addSymbolToList(list.id, sym)}
                                disabled={alreadyInList}
                                className={cn(
                                  'text-[10px] px-2 py-0.5 rounded cursor-pointer whitespace-nowrap transition-all',
                                  alreadyInList
                                    ? 'text-foreground-300 cursor-not-allowed'
                                    : 'text-accent-400 hover:bg-accent-500/10'
                                )}
                              >
                                {alreadyInList ? 'Added' : '+ Add'}
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {availableSymbols.length === 0 && !symbolsLoading && (
                      <p className="text-foreground-500 text-[10px] text-center py-3">
                        Click &quot;Fetch Symbols&quot; to browse available symbols from cTrader.
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Symbol chips — collapsed or expanded */}
              {list.symbols.length > 0 && (
                <div className="px-4 pb-3">
                  {!isExpanded ? (
                    <button
                      type="button"
                      onClick={() => toggleExpanded(list.id)}
                      className="text-xs font-medium text-accent-500 hover:text-accent-600 cursor-pointer transition-colors flex items-center gap-1.5 whitespace-nowrap"
                    >
                      <i className="ri-eye-line text-sm"></i>
                      See this List
                      <span className="text-[10px] text-foreground-400 font-normal">
                        ({list.symbols.length} symbols)
                      </span>
                    </button>
                  ) : (
                    <>
                      <div className="flex flex-wrap gap-1.5">
                        {list.symbols.map((sym) => (
                          <span
                            key={sym.symbolId}
                            className="inline-flex items-center gap-1 px-2 py-1 bg-background-100 border border-background-200/70 rounded-md text-[10px] group"
                          >
                            <span className="text-foreground-800 font-mono font-medium">{sym.symbolName}</span>
                            <span className="text-foreground-400 font-mono">#{sym.symbolId}</span>
                            <button
                              type="button"
                              onClick={() => removeSymbolFromList(list.id, sym.symbolId)}
                              className="text-foreground-400 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-all cursor-pointer ml-0.5"
                            >
                              <i className="ri-close-line text-[9px]"></i>
                            </button>
                          </span>
                        ))}
                      </div>
                      <button
                        type="button"
                        onClick={() => toggleExpanded(list.id)}
                        className="mt-2 text-[10px] text-foreground-400 hover:text-foreground-600 cursor-pointer transition-colors flex items-center gap-1 whitespace-nowrap"
                      >
                        <i className="ri-eye-off-line"></i>
                        Hide list
                      </button>
                    </>
                  )}
                </div>
              )}

              {list.symbols.length === 0 && showSymbolBrowser !== list.id && (
                <div className="px-4 pb-3">
                  <p className="text-foreground-400 text-[10px] italic">No symbols in this list. Click + to add.</p>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}